/**
 * @file filterUtils.ts
 * @desc Utility functions for custom filter component
 * @copyright All rights reserved @ Inarix
 */
import { TFunction } from "i18next";
import { FilterItemExtended, ApiOption } from "../types";

export const getColumnType = (columns: any[], field: string): string => {
  const column = columns.find((col) => col.field === field);
  return column ? column.columnType : "";
};

export const getTranslatedOperatorLabel = (
  operatorValue: string,
  t: TFunction<"global", undefined>,
): string => {
  switch (operatorValue) {
    case "$ilike":
      return t("custom_filter.operators.contains");
    case "$eq":
      return t("custom_filter.operators.equals");
    case "$gt":
      return t("custom_filter.operators.greater_than");
    case "$lt":
      return t("custom_filter.operators.less_than");
    case "$ne":
      return t("custom_filter.operators.not_equal");
    case "$in":
      return t("custom_filter.operators.is_any_of");
    default:
      return operatorValue;
  }
};

export const getAvailableOperators = (
  fieldName: string,
  columns: any[],
): any[] => {
  if (!fieldName) return [];
  const column = columns.find((col) => col.field === fieldName);
  return column?.filterOperators || [];
};

export const getFilterableColumns = (columns: any[]): any[] => {
  return columns.filter(
    (col) => col.filterable !== false && col.filterOperators?.length > 0,
  );
};

export const getChipValues = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value
    ? value
        .split(",")
        .map((v: string) => v.trim())
        .filter(Boolean)
    : [];
};

export const formatOptionLabel = (
  opt: ApiOption,
  isActionsFilter: boolean,
): string => {
  if (isActionsFilter && opt.cerealType) {
    return `${opt.name} (${opt.cerealType})`;
  }
  return opt.name;
};

export const createEmptyFilterItem = (): FilterItemExtended => ({
  field: "",
  operator: "",
  value: "",
  column: "",
  realOperator: "",
  columnType: "",
  filter: "",
  service: "",
  queryFormat: "",
});

export const convertIdsToNames = (
  item: FilterItemExtended,
  locations: ApiOption[],
  column: any,
): FilterItemExtended => {
  const queryFormat = item.queryFormat || column?.queryFormat;

  if (
    queryFormat &&
    typeof item.value === "string" &&
    item.value.includes(queryFormat)
  ) {
    const matches = item.value.match(/=(\d+)/g);
    if (matches) {
      const ids = matches.map((match) => Number(match.substring(1)));
      const names = ids
        .map((id) => locations.find((loc) => loc.id === id)?.name)
        .filter((name) => name !== undefined);

      if (names.length > 0) {
        return { ...item, value: names };
      }
    }
    return item;
  }

  let values: any[];
  if (Array.isArray(item.value)) {
    values = item.value;
  } else {
    values = item.value.split(",").map((v) => v.trim());
  }

  const firstValue = values[0];

  if (firstValue && !Number.isNaN(Number(firstValue))) {
    const names = values
      .map((id) => locations.find((loc) => loc.id === Number(id))?.name)
      .filter((name) => name !== undefined);

    if (names.length > 0) {
      return { ...item, value: names };
    }
  }

  return item;
};

export const convertActionsIdsToNames = (
  item: FilterItemExtended,
  actions: ApiOption[],
): FilterItemExtended => {
  let values: any[];
  if (Array.isArray(item.value)) {
    values = item.value;
  } else {
    values = item.value.split(",").map((v) => v.trim());
  }

  const firstValue = values[0];

  if (firstValue && !Number.isNaN(Number(firstValue))) {
    const formattedNames = values
      .map((id) => {
        const action = actions.find((act) => act.id === Number(id));
        if (action) {
          return action.cerealType
            ? `${action.name} (${action.cerealType})`
            : action.name;
        }
        return undefined;
      })
      .filter((name) => name !== undefined);

    if (formattedNames.length > 0) {
      return { ...item, value: formattedNames };
    }
  }

  if (
    firstValue &&
    typeof firstValue === "string" &&
    Number.isNaN(Number(firstValue))
  ) {
    const formattedNames = values
      .map((name) => {
        const plainName = name.match(/^(.+?)\s*\(/)
          ? name.match(/^(.+?)\s*\(/)[1]
          : name;
        const action = actions.find((act) => act.name === plainName);
        if (action) {
          return action.cerealType
            ? `${action.name} (${action.cerealType})`
            : action.name;
        }
        return name;
      })
      .filter((name) => name !== undefined);

    if (formattedNames.length > 0) {
      return { ...item, value: formattedNames };
    }
  }

  return item;
};

export const processApiFilter = (
  item: FilterItemExtended,
  index: number,
  apiOptions: { [key: number]: any[] },
  columns: any[],
  comesFromDashboard?: boolean,
): FilterItemExtended => {
  const column = columns.find((col) => col.field === item.field);

  if (item.operator !== "$in") {
    const options = apiOptions[index] || [];
    if (!isNaN(Number(item.value))) {
      return {
        ...item,
        filter: item.filter || column?.filter,
        service: item.service || column?.service,
        queryFormat: item.queryFormat || column?.queryFormat,
      };
    }

    const selectedName = item.value;
    const selectedOption = options.find((opt) => opt.name === selectedName);
    const selectedId = selectedOption ? selectedOption.id : undefined;

    return {
      ...item,
      value: selectedId !== undefined ? selectedId : item.value,
      filter: item.filter || column?.filter,
      service: item.service || column?.service,
      queryFormat: item.queryFormat || column?.queryFormat,
    };
  }

  const options = apiOptions[index] || [];
  const selectedNames = Array.isArray(item.value)
    ? item.value
    : item.value
      ? item.value.split(",").map((v) => v.trim())
      : [];

  if (comesFromDashboard) {
    const selectedIds = selectedNames
      .map((name) => options.find((opt) => opt.name === name)?.id)
      .filter((id) => id !== undefined);

    return {
      ...item,
      value: selectedNames.join(","),
      filter: item.filter || column?.filter,
      service: item.service || column?.service,
      secondValues: selectedIds,
      queryFormat: item.queryFormat || column?.queryFormat,
    };
  }

  const selectedIds = selectedNames
    .map((name) => options.find((opt) => opt.name === name)?.id)
    .filter((id) => id !== undefined);

  const queryFormat = item.queryFormat || column?.queryFormat;
  let formattedValue;

  if (queryFormat && selectedIds.length > 0) {
    formattedValue = selectedIds.map((id) => `${queryFormat}${id}`).join("&");
  } else {
    formattedValue = selectedIds;
  }

  return {
    ...item,
    value: formattedValue,
    filter: item.filter || column?.filter,
    service: item.service || column?.service,
    queryFormat: item.queryFormat || column?.queryFormat,
  };
};

export const processLocalFilter = (
  item: FilterItemExtended,
  index: number,
  apiOptions: { [key: number]: any[] },
  columns: any[],
): FilterItemExtended => {
  const column = columns.find((col) => col.field === item.field);
  const options = apiOptions[index] || [];

  const selectedNames = Array.isArray(item.value)
    ? item.value
    : item.value
      ? item.value.split(",").map((v) => v.trim())
      : [];

  const selectedIds = selectedNames
    .map((displayName) => {
      const match = displayName.match(/^(.+?)\s*\((.+?)\)$/);
      if (match) {
        const plainName = match[1];
        const cerealType = match[2];
        return options.find(
          (opt) => opt.name === plainName && opt.cerealType === cerealType,
        )?.id;
      } else {
        return options.find((opt) => opt.name === displayName)?.id;
      }
    })
    .filter((id) => id !== undefined);

  return {
    ...item,
    value: selectedIds,
    filter: item.filter || column?.filter,
    service: item.service || column?.service,
  };
};
